import { JaneGoldTag, getApplicableGoldTagLabel } from '@iheartjane/menu';
import {
  Button,
  DismissIcon,
  Flex,
  Typography,
  useMobileMediaQuery,
} from '@iheartjane/reefer';
import type { PriceId } from '@iheartjane/types';
import { formatCurrency } from '@iheartjane/utils';

import { useShouldShowGold } from '@jane/shared/hooks';
import { getMaxCartQuantity } from '@jane/shared/util';

import { DisplayMode, useProductCardContext } from '../../productCardProvider';
import { QuantitySelector } from '../../quantitySelector/quantitySelector';

interface EditingCardProps {
  decrementQuantity: () => void;
  discountedPrice?: number;
  incrementQuantity: () => void;
  multipleCountDisabled: boolean;
  onAddToCartPressed: () => void;
  originalPrice?: number;
  selectedQuantity: number;
  selectedWeight: PriceId;
  shoppingDisabled: boolean;
  soldByWeight: boolean;
}

const EditingCard = ({
  decrementQuantity,
  discountedPrice,
  incrementQuantity,
  multipleCountDisabled,
  onAddToCartPressed,
  originalPrice,
  selectedQuantity,
  selectedWeight,
  shoppingDisabled,
  soldByWeight,
}: EditingCardProps) => {
  const smallerThanVerticalTablet = useMobileMediaQuery({});

  const {
    brandSpecials,
    cartProduct,
    menuProduct,
    setDisplayMode,
    store,
    trackListViewClick,
  } = useProductCardContext();

  const shouldShowGold = useShouldShowGold({ product: menuProduct, store });
  const price = discountedPrice || originalPrice;
  const taxText = store?.tax_included ? `Taxes included` : '';

  const selectedWeightInCart = cartProduct?.find(
    (product) => product.price_id === selectedWeight
  );

  const alreadyInCart =
    selectedWeight === selectedWeightInCart?.price_id &&
    selectedQuantity === selectedWeightInCart?.count;

  let buttonText = shoppingDisabled
    ? 'Shopping disabled'
    : cartProduct && selectedQuantity === 0
      ? 'Remove'
      : cartProduct && selectedWeightInCart
        ? 'Update'
        : soldByWeight && !selectedWeight
          ? `Select a weight`
          : `Confirm`;

  const productLabelWithWeight = `${soldByWeight ? `${selectedWeight} ` : ''}${
    menuProduct?.name
  }`;
  const productWithWeightQuantityAndPrice = `${selectedQuantity} of ${productLabelWithWeight} for ${formatCurrency(
    price
  )}`;
  const buttonAriaLabelText = shoppingDisabled
    ? 'Shopping disabled'
    : cartProduct && selectedQuantity === 0
      ? `Remove ${productLabelWithWeight}`
      : cartProduct && selectedWeightInCart
        ? `Update ${productLabelWithWeight} to ${productWithWeightQuantityAndPrice}`
        : soldByWeight && !selectedWeight
          ? `Select a weight of ${menuProduct?.name}`
          : `Confirm adding ${productWithWeightQuantityAndPrice}`;

  if (smallerThanVerticalTablet) {
    buttonText = `${formatCurrency(price)} - ${buttonText}`;
  }

  const janeGoldTagLabel =
    menuProduct && shouldShowGold
      ? getApplicableGoldTagLabel({
          brandSpecials: brandSpecials || [],
          applicableBrandSpecialIds:
            menuProduct.applicable_brand_special_ids as number[],
          priceId: selectedWeight,
        })
      : null;

  return (
    <Flex
      alignItems="center"
      background="grays-ultralight"
      justifyContent="flex-end"
      flexWrap="wrap"
      p={24}
      gap={16}
    >
      <Flex
        onClick={() => {
          setDisplayMode(
            (cartProduct || []).length > 0
              ? DisplayMode.Confirmation
              : DisplayMode.Product
          );
          trackListViewClick && trackListViewClick();
        }}
        justifyContent="center"
        ariaLabel="close"
        justifySelf="flex-start"
        mr="auto"
      >
        <DismissIcon color="text-main" />
      </Flex>
      {!smallerThanVerticalTablet && (
        <Flex height="100%" alignItems="center">
          {janeGoldTagLabel && <JaneGoldTag label={janeGoldTagLabel} mr={16} />}
          <Flex
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            mr={16}
            ml={4}
          >
            {price !== undefined && !isNaN(price) && (
              <Typography branded variant="header-bold">
                {formatCurrency(price)}
              </Typography>
            )}
            <Typography variant="body">{taxText}</Typography>
          </Flex>
        </Flex>
      )}

      <Flex justifyContent="flex-end">
        {!multipleCountDisabled && (
          <Flex justifyContent="center" pr={16}>
            <QuantitySelector
              minQuantity={selectedWeightInCart ? 0 : 1}
              maxQuantity={
                menuProduct && getMaxCartQuantity(menuProduct, selectedWeight)
              }
              value={selectedQuantity}
              incrementQuantity={incrementQuantity}
              decrementQuantity={decrementQuantity}
            />
          </Flex>
        )}
      </Flex>
      {price !== undefined && !isNaN(price) && (
        <Button
          full={smallerThanVerticalTablet}
          label={buttonText}
          variant="primary"
          onClick={onAddToCartPressed}
          disabled={
            shoppingDisabled ||
            (soldByWeight && !selectedWeight) ||
            alreadyInCart
          }
          ariaLabel={buttonAriaLabelText}
        />
      )}
    </Flex>
  );
};

export default EditingCard;
